<template>
  <div class="transition">
    <BasicText size="h5">Pour quelle demande de réservation de fonds (DRF) ?</BasicText>
    <div
      class="transition-items"
      style="max-width: 600px"
    >
      <WrapperAutocomplete
        v-if="!isClone"
        v-model="data"
        :search
        key-id="code"
        key-label="code"
        placeholder="Numéro de DRF ou référence projet"
        :search-min-length="3"
        strict
        :emptyResultLabel="msg"
      >
        <template v-slot="item">
          <div class="item">
            <BasicText
              weight="bold"
              size="body-m"
            >
              {{ item.code }} - {{ item.intitule }}
            </BasicText>

            <BasicText size="body-s">{{ dateFormatter(item.dateDebut) }} - {{ dateFormatter(item.dateFin) }}</BasicText>
          </div>
        </template>
      </WrapperAutocomplete>
      <WrapperInput
        v-else
        id="field_codeDrf"
        class="col-sm-36"
        v-model="props.in.codeDrf!"
        disabled
      />
      <BasicAlert
        v-if="messageDrf"
        :alert-label="messageDrf"
        alert-type="danger"
      ></BasicAlert>
      <BasicAlert
        v-else
        alert-label="Veuillez saisir le numéro de la DRF ou le code de la référence du projet."
        alert-type="info"
      ></BasicAlert>
      <BasicAlert
        v-if="MSG_DRF_DEROGATION"
        alertType="warning"
        :alertLabel="MSG_DRF_DEROGATION"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RechercheResultDossierReservationFondsDTO } from '@/features/api/extranet/dto/Dossier/Recherche/ReservationFonds'
  import type {
    ChoixReservationFondsIn,
    ChoixReservationFondsOut,
  } from '@/features/interface/demande/ajouter/cartes/ChoixReservationFonds.types.ts'
  import type { CarteEvents, CarteProps } from '@/features/interface/machine-etats/types.ts'
  import {
    dateFormatter,
    isNotNullOrUndefined,
    isNotNullUndefinedOrEmptyString,
    isNullUndefinedOrEmptyString,
  } from '@/features/shared/tools'
  import { computed, onBeforeMount, ref } from 'vue'
  import { MSG_DRF_DEROGATION } from '../../shared'

  const props = defineProps<CarteProps<ChoixReservationFondsIn, ChoixReservationFondsOut>>()
  const emits = defineEmits<CarteEvents<ChoixReservationFondsOut>>()
  const selected = ref<RechercheResultDossierReservationFondsDTO | undefined>(undefined)
  const messageDrf = computed(() => {
    if (isNullUndefinedOrEmptyString(data.value?.niveauPermissionCxpi)) return undefined
    if (data.value?.niveauPermissionCxpi === 'Ecriture') return undefined
    if (props.in.drfDossiers.length === 0) return msg
    return data.value?.commentaireBlocagePermissionCxpi ?? msg
  })
  const msg = `La DRF saisie est introuvable ou aucune DRF valide n'existe pour l'établissement sélectionné.
     Veuillez vérifier que le numéro saisi est correct et que la DRF est bien accordée et en cours de validité.` as const

  const data = computed<RechercheResultDossierReservationFondsDTO | undefined>({
    get: () => selected.value,
    set: (item) => {
      selected.value = item
      const isValid = isNotNullOrUndefined(item) && item.niveauPermissionCxpi === 'Ecriture'
      const result: Partial<ChoixReservationFondsOut> = {
        code: item?.code,
        dispositif: item?.dispositif,
        sousDispositif: item?.sousDispositif,
      }
      emits('outChanged', isValid, result as ChoixReservationFondsOut)
    },
  })

  const search = (query: string) => {
    query = query.trim().toLowerCase()
    if (isNullUndefinedOrEmptyString(query)) return props.in.drfDossiers
    return props.in.drfDossiers.filter(
      (drf) => drf.code?.toLocaleLowerCase()?.includes(query) || drf.codeDemande?.toLocaleLowerCase()?.includes(query),
    )
  }

  const isClone = computed<boolean>(() => isNotNullUndefinedOrEmptyString(props.in.codeDrf))

  onBeforeMount(async () => {
    if (isClone.value) data.value = props.in.drfDossiers.at(0)
  })
</script>
<style lang="less" scoped>
  @import './ChoixCarte.less';
</style>
